/*
 * ExecutiveSummaryModal — answers one executive question:
 *   "Expected Due was Rs.X on day 1. It is Rs.Y today. WHY?"
 *
 * Everything is driven by the backend reconciliation bridge
 * (audit.bridge.expected_due / audit.bridge.promised) whose lines always sum
 * exactly to (close - open). Anything unattributable surfaces as a red
 * "Unexplained" line instead of being hidden.
 */
import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  FileText, AlertTriangle, Info, ChevronRight, ChevronDown, ArrowRight,
  Banknote, CalendarClock, ReceiptText, Ban, Plus, HelpCircle,
} from 'lucide-react';

const fmtInr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const compact = (n) => {
  const v = Number(n) || 0, a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (a >= 1000) return `₹${Math.round(v / 1000)}k`;
  return fmtInr(v);
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN',
      { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
};
const monthLabel = (iso) => {
  if (!iso) return 'No date';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  } catch { return iso; }
};

// Presentation metadata per bridge line key. Keys mirror _BRIDGE_META in
// backend/slip_tracker_audit_routes.py — keep in sync.
const LINE_UI = {
  cash_collected:  { icon: Banknote,      tone: 'emerald', group: 'cash' },
  credit_notes:    { icon: ReceiptText,   tone: 'violet',  group: 'cn' },
  write_off:       { icon: Ban,           tone: 'slate',   group: 'lost' },
  voided:          { icon: Ban,           tone: 'slate',   group: 'lost' },
  date_moved_out:  { icon: CalendarClock, tone: 'rose',    group: 'slipped' },
  scope_out:       { icon: ArrowRight,    tone: 'slate',   group: 'lost' },
  unexplained_out: { icon: AlertTriangle, tone: 'red',     group: 'unexplained' },
  new_invoice:     { icon: Plus,          tone: 'sky',     group: 'entered' },
  date_moved_in:   { icon: CalendarClock, tone: 'sky',     group: 'entered' },
  scope_in:        { icon: ArrowRight,    tone: 'sky',     group: 'entered' },
  value_increased: { icon: Plus,          tone: 'amber',   group: 'entered' },
  unexplained_in:  { icon: AlertTriangle, tone: 'red',     group: 'unexplained' },
};

const TONE = {
  emerald: { bar: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  violet:  { bar: 'bg-violet-500',  text: 'text-violet-700',  chip: 'bg-violet-50 border-violet-200 text-violet-800' },
  rose:    { bar: 'bg-rose-500',    text: 'text-rose-700',    chip: 'bg-rose-50 border-rose-200 text-rose-800' },
  red:     { bar: 'bg-red-600',     text: 'text-red-700',     chip: 'bg-red-50 border-red-300 text-red-800' },
  slate:   { bar: 'bg-slate-400',   text: 'text-slate-600',   chip: 'bg-slate-50 border-slate-200 text-slate-700' },
  sky:     { bar: 'bg-sky-500',     text: 'text-sky-700',     chip: 'bg-sky-50 border-sky-200 text-sky-800' },
  amber:   { bar: 'bg-amber-500',   text: 'text-amber-700',   chip: 'bg-amber-50 border-amber-200 text-amber-800' },
};

const METRICS = [
  { key: 'expected_due', label: 'Expected Due',
    blurb: 'Money still outstanding on invoices promised in your window.' },
  { key: 'promised', label: 'Promised',
    blurb: 'Total ₹ value of promises sitting inside your window (balance + cash already received in-window).' },
];

export default function ExecutiveSummaryModal({ open, onOpenChange, audit, snapFrom, snapTo, rangeFrom, rangeTo, onOpenInvoice }) {
  const [metric, setMetric] = useState('expected_due');
  const [expanded, setExpanded] = useState({});

  const bridge = audit?.bridge?.[metric] || null;

  const stats = useMemo(() => {
    if (!bridge) return null;
    const lines = bridge.lines || [];
    const outs = lines.filter((l) => l.amount < 0);
    const ins  = lines.filter((l) => l.amount > 0);
    const grossOut = outs.reduce((s, l) => s + Math.abs(l.amount), 0);
    const grossIn  = ins.reduce((s, l) => s + l.amount, 0);
    const byKey = Object.fromEntries(lines.map((l) => [l.key, l]));
    const cash = Math.abs(byKey.cash_collected?.amount || 0);
    const cashShare = grossOut > 0 ? cash / grossOut : 0;
    const nonCash = outs.filter((l) => l.key !== 'cash_collected')
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    const unexplained = Math.abs(byKey.unexplained_out?.amount || 0) + Math.abs(byKey.unexplained_in?.amount || 0);
    const max = Math.max(grossOut, grossIn, 1);
    const dropPct = bridge.open > 0 ? Math.abs(bridge.delta) / bridge.open : 0;
    return { lines, outs, ins, grossOut, grossIn, cash, cashShare, nonCash, unexplained, max, dropPct };
  }, [bridge]);

  // Salesperson + actor accountability, derived from the same bridge rows so
  // the tables can never disagree with the waterfall.
  const people = useMemo(() => {
    if (!bridge) return { leaderboard: [], actors: [], repeatCust: [] };
    const sp = {}, act = {}, cust = {};
    for (const l of bridge.lines) {
      const grp = LINE_UI[l.key]?.group || 'other';
      for (const r of l.rows) {
        const s = sp[r.salesperson_name] || (sp[r.salesperson_name] = { name: r.salesperson_name, cash: 0, cn: 0, slipped: 0, lost: 0, unexplained: 0 });
        if (s[grp] !== undefined) s[grp] += r.amount;
        if (['cn', 'slipped', 'lost', 'unexplained'].includes(grp)) {
          const c = cust[r.customer_name] || (cust[r.customer_name] = { customer: r.customer_name, groups: new Set(), amount: 0 });
          c.groups.add(grp); c.amount += r.amount;
        }
        if (r.actor && ['cn', 'slipped'].includes(grp)) {
          const a = act[r.actor] || (act[r.actor] = { actor: r.actor, cn: 0, cnValue: 0, slipped: 0, slippedValue: 0 });
          if (grp === 'cn') { a.cn++; a.cnValue += r.amount; } else { a.slipped++; a.slippedValue += r.amount; }
        }
      }
    }
    const leaderboard = Object.values(sp).map((s) => {
      const cosmetic = s.cn + s.slipped + s.lost + s.unexplained;
      const total = s.cash + cosmetic;
      return { ...s, cosmetic, total, cashShare: total > 0 ? s.cash / total : 0 };
    }).filter((s) => s.total > 0).sort((a, b) => a.cashShare - b.cashShare);
    const actors = Object.values(act).sort((a, b) => (b.cnValue + b.slippedValue) - (a.cnValue + a.slippedValue)).slice(0, 6);
    const repeatCust = Object.values(cust).filter((c) => c.groups.size >= 2)
      .sort((a, b) => b.amount - a.amount).slice(0, 5);
    return { leaderboard, actors, repeatCust };
  }, [bridge]);

  if (!audit || !bridge || !stats) return null;

  const dropping = bridge.delta < 0;
  const topNonCash = stats.nonCash[0];
  const nonCashPct = stats.grossOut > 0 ? Math.round(100 * (1 - stats.cashShare)) : 0;

  let verdict;
  if (stats.grossOut < 1 && stats.grossIn < 1) {
    verdict = { tone: 'slate', label: 'No movement', desc: 'Nothing material moved between these two snapshots.' };
  } else if (metric === 'promised') {
    // Promised is invariant to cash (balance + received-in-window), so the only
    // meaningful question is whether the promise book grew or leaked.
    verdict = dropping
      ? { tone: 'rose', label: 'Promise book shrank',
          desc: `${compact(Math.abs(bridge.delta))} of promises left your window${topNonCash ? ` — largest cause is "${topNonCash.label}" at ${compact(Math.abs(topNonCash.amount))}` : ''}. Cash is not counted here: a payment never shrinks a promise.` }
      : { tone: 'emerald', label: 'Promise book grew',
          desc: `${compact(stats.grossIn)} of promises came into the window against ${compact(stats.grossOut)} that left. Cash is not counted here: a payment never shrinks a promise.` };
  } else if (stats.cashShare >= 0.6) {
    verdict = { tone: 'emerald', label: 'Healthy — mostly real cash',
      desc: `${Math.round(stats.cashShare * 100)}% of the reduction is money in the bank.` };
  } else if (stats.cashShare >= 0.3) {
    verdict = { tone: 'amber', label: 'Mixed — cash plus paperwork',
      desc: `${nonCashPct}% of the reduction is non-cash${topNonCash ? ` — largest is "${topNonCash.label}" at ${compact(Math.abs(topNonCash.amount))}` : ''}.` };
  } else {
    verdict = { tone: 'rose', label: 'Cosmetic — this is not collection',
      desc: `${nonCashPct}% of the ${compact(Math.abs(bridge.delta))} movement is NOT cash${topNonCash ? ` — ${compact(Math.abs(topNonCash.amount))} is "${topNonCash.label}"` : ''}. Investigate before celebrating.` };
  }
  const vTone = {
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-300 bg-amber-50 text-amber-900',
    rose: 'border-rose-300 bg-rose-50 text-rose-900',
    slate: 'border-slate-300 bg-slate-50 text-slate-700',
  }[verdict.tone];

  const headline = (() => {
    const m = METRICS.find((x) => x.key === metric).label;
    const dir = dropping ? 'fell' : 'rose';
    const parts = [
      `${m} ${dir} by ${compact(Math.abs(bridge.delta))} (${Math.round(stats.dropPct * 100)}%) between ${fmtDate(snapFrom)} and ${fmtDate(snapTo)}.`,
    ];
    if (stats.cash > 0) parts.push(`${compact(stats.cash)} of that is real cash collected.`);
    for (const l of stats.nonCash.slice(0, 2)) {
      parts.push(`${compact(Math.abs(l.amount))} is ${l.label.toLowerCase()} (${l.count} invoice${l.count > 1 ? 's' : ''}).`);
    }
    if (stats.grossIn > 0) parts.push(`${compact(stats.grossIn)} of fresh promises came into the window.`);
    return parts.join(' ');
  })();

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  // Destination-month rollup for date moves — "where did the promises go?"
  const monthRollup = (line) => {
    const key = line.key === 'date_moved_out' ? 'to_date' : 'from_date';
    const m = {};
    for (const r of line.rows) {
      const k = monthLabel(r[key]);
      m[k] = (m[k] || 0) + r.amount;
    }
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };

  const exportPdf = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'), import('jspdf-autotable'),
    ]);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const M = 36;
    const ascii = (s) => String(s || '').replace(/[₹]/g, 'Rs.').replace(/[→–—]/g, '-').replace(/[^\x20-\x7E]/g, '');
    const label = METRICS.find((x) => x.key === metric).label;

    doc.setFontSize(15); doc.setTextColor(15, 23, 42);
    doc.text(ascii(`Slip Tracker - ${label} Reconciliation`), M, 46);
    doc.setFontSize(10); doc.setTextColor(90);
    doc.text(ascii(`Snapshots ${snapFrom} -> ${snapTo}  |  Window ${rangeFrom} -> ${rangeTo}`), M, 62);
    doc.text(ascii(`Generated ${new Date().toLocaleString('en-IN')}`), M, 76);

    doc.setFillColor(...(verdict.tone === 'emerald' ? [16, 185, 129] : verdict.tone === 'amber' ? [217, 119, 6] : verdict.tone === 'rose' ? [244, 63, 94] : [148, 163, 184]));
    doc.roundedRect(M, 90, pageW - M * 2, 46, 4, 4, 'F');
    doc.setTextColor(255); doc.setFontSize(11);
    doc.text(ascii(verdict.label), M + 12, 107);
    doc.setFontSize(9);
    doc.text(ascii(headline), M + 12, 122, { maxWidth: pageW - M * 2 - 24 });

    const body = [
      [ascii(`${label} on ${snapFrom} (opening)`), ascii(fmtInr(bridge.open)), '', ''],
      ...stats.lines.map((l) => [
        ascii(l.label),
        ascii((l.amount < 0 ? '- ' : '+ ') + fmtInr(Math.abs(l.amount))),
        String(l.count),
        ascii(l.hint),
      ]),
      [ascii(`${label} on ${snapTo} (closing)`), ascii(fmtInr(bridge.close)), '', ''],
      ['Unreconciled parity gap', ascii(fmtInr(bridge.parity_gap)), '',
        bridge.parity_gap === 0 ? 'Exact parity - every rupee explained' : 'Investigate'],
    ];
    autoTable(doc, {
      startY: 150,
      head: [['Bridge component', 'Amount', 'Inv', 'What it means']],
      body,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255 },
      columnStyles: { 1: { halign: 'right', cellWidth: 78 }, 2: { halign: 'right', cellWidth: 30 } },
      didParseCell: (d) => {
        if (d.section === 'body' && (d.row.index === 0 || d.row.index === body.length - 2)) {
          d.cell.styles.fontStyle = 'bold'; d.cell.styles.fillColor = [241, 245, 249];
        }
      },
    });

    for (const l of stats.lines) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 18,
        head: [[ascii(`${l.label} - ${fmtInr(Math.abs(l.amount))} across ${l.count} invoice(s)`), '', '', '', '']],
        body: [
          ['Invoice', 'Customer', 'Salesperson', 'Promise date', 'Amount'],
          ...l.rows.map((r) => [
            ascii(r.invoice_number), ascii(r.customer_name), ascii(r.salesperson_name),
            ascii(r.from_date === r.to_date ? fmtDate(r.from_date) : `${fmtDate(r.from_date)} -> ${fmtDate(r.to_date)}${r.actor ? ` (${r.actor})` : ''}`),
            ascii(fmtInr(r.amount)),
          ]),
        ],
        styles: { fontSize: 7.5, cellPadding: 3 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontSize: 8.5, halign: 'left' },
        columnStyles: { 4: { halign: 'right', cellWidth: 62 } },
        didParseCell: (d) => {
          if (d.section === 'body' && d.row.index === 0) {
            d.cell.styles.fontStyle = 'bold'; d.cell.styles.fillColor = [241, 245, 249];
          }
        },
      });
    }

    if (people.leaderboard.length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 18,
        head: [['Salesperson', 'Cash in', 'Credit notes', 'Pushed out', 'Cash-share']],
        body: people.leaderboard.map((s) => [
          ascii(s.name), ascii(fmtInr(s.cash)), ascii(fmtInr(s.cn)),
          ascii(fmtInr(s.slipped)), `${Math.round(s.cashShare * 100)}%`,
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
      });
    }
    doc.save(`slip_${metric}_reconciliation_${snapFrom}_to_${snapTo}.pdf`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="stba-exec-summary">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2 pr-8">
            <div>
              <DialogTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-violet-600" /> Executive Summary · What changed and why
              </DialogTitle>
              <div className="text-[11px] text-slate-500 mt-1">
                {snapFrom} → {snapTo} · Window {rangeFrom} → {rangeTo}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={exportPdf} data-testid="stba-exec-pdf"
                    className="h-7 gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50">
              <FileText className="w-3.5 h-3.5" /> PDF
            </Button>
          </div>
        </DialogHeader>

        {/* Metric toggle */}
        <div className="flex items-center gap-1.5">
          {METRICS.map((m) => (
            <button key={m.key} type="button" onClick={() => { setMetric(m.key); setExpanded({}); }}
              data-testid={`stba-metric-${m.key}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
                metric === m.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
              {m.label} bridge
            </button>
          ))}
          <span className="text-[10px] text-slate-400 ml-1 hidden sm:inline">
            {METRICS.find((m) => m.key === metric).blurb}
          </span>
        </div>

        {/* Open → Close headline */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid="stba-bridge-headline">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">On {fmtDate(snapFrom)}</div>
              <div className="text-2xl font-bold tabular-nums text-slate-800">{fmtInr(bridge.open)}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 mb-2" />
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">On {fmtDate(snapTo)}</div>
              <div className="text-2xl font-bold tabular-nums text-slate-800">{fmtInr(bridge.close)}</div>
            </div>
            <div className={`ml-auto text-right ${dropping ? 'text-rose-700' : 'text-emerald-700'}`}>
              <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">Change</div>
              <div className="text-xl font-bold tabular-nums">
                {dropping ? '▼' : '▲'} {fmtInr(Math.abs(bridge.delta))}
              </div>
              <div className="text-[10px] opacity-80">{Math.round(stats.dropPct * 100)}% of opening</div>
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div className={`rounded-lg border p-3 ${vTone}`} data-testid="stba-verdict">
          <div className="text-[13px] font-bold">{verdict.label}</div>
          <div className="text-[11px] mt-0.5">{verdict.desc}</div>
        </div>

        {/* Plain-English narrative */}
        <div className="rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2 text-[12px] text-violet-900 leading-relaxed"
             data-testid="stba-diagnosis">
          <div className="text-[10px] font-bold uppercase tracking-wide text-violet-700 mb-0.5">In plain English</div>
          {headline}
        </div>

        {/* Bridge waterfall */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Reconciliation bridge · click any line for the invoices
            </div>
            <div className={`text-[10px] font-semibold ${bridge.parity_gap === 0 ? 'text-emerald-700' : 'text-red-700'}`}
                 data-testid="stba-parity">
              {bridge.parity_gap === 0 ? 'Exact parity · every ₹ explained' : `Parity gap ${fmtInr(bridge.parity_gap)}`}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-100 text-[11px] font-bold text-slate-700">
              <span>Opening · {fmtDate(snapFrom)}</span>
              <span className="tabular-nums">{fmtInr(bridge.open)}</span>
            </div>

            {stats.lines.map((l) => {
              const ui = LINE_UI[l.key] || { icon: HelpCircle, tone: 'slate' };
              const t = TONE[ui.tone];
              const Icon = ui.icon;
              const mag = Math.abs(l.amount);
              const pctOfSide = (l.amount < 0 ? stats.grossOut : stats.grossIn) > 0
                ? Math.round(100 * mag / (l.amount < 0 ? stats.grossOut : stats.grossIn)) : 0;
              const isOpen = !!expanded[l.key];
              const isDateMove = l.key === 'date_moved_out' || l.key === 'date_moved_in';
              return (
                <div key={l.key} className="border-t border-slate-100">
                  <button type="button" onClick={() => toggle(l.key)}
                    data-testid={`stba-bridge-line-${l.key}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${t.text}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-semibold text-slate-800 flex items-center gap-1.5">
                          {l.label}
                          {ui.tone === 'red' && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-600 text-white">
                              needs investigation
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{l.hint}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-[13px] font-bold tabular-nums ${t.text}`}>
                          {l.amount < 0 ? '−' : '+'} {fmtInr(mag)}
                        </div>
                        <div className="text-[10px] text-slate-500">{l.count} inv · {pctOfSide}%</div>
                      </div>
                    </div>
                    <div className="mt-1.5 ml-8 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${t.bar}`} style={{ width: `${Math.max(2, 100 * mag / stats.max)}%` }} />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="bg-slate-50/60 border-t border-slate-100 px-3 py-2">
                      {isDateMove && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <span className="text-[10px] text-slate-500 font-semibold self-center">
                            {l.key === 'date_moved_out' ? 'Where the promises went:' : 'Where they came from:'}
                          </span>
                          {monthRollup(l).map(([m, amt]) => (
                            <span key={m} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${t.chip}`}>
                              {m} · {compact(amt)}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="text-[9px] uppercase text-slate-500">
                              <th className="text-left py-1 pr-2">Invoice</th>
                              <th className="text-left py-1 pr-2">Customer</th>
                              <th className="text-left py-1 pr-2">Salesperson</th>
                              <th className="text-left py-1 pr-2">Promise date</th>
                              <th className="text-right py-1">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {l.rows.map((r, i) => (
                              <tr key={i} className="border-t border-slate-200/70 hover:bg-violet-50 cursor-pointer"
                                  onClick={() => onOpenInvoice?.(r)}
                                  data-testid={`stba-bridge-row-${l.key}-${i}`}>
                                <td className="py-1 pr-2 font-medium text-violet-700 whitespace-nowrap">{r.invoice_number}</td>
                                <td className="py-1 pr-2 text-slate-700">{r.customer_name}</td>
                                <td className="py-1 pr-2 text-slate-500">{r.salesperson_name}</td>
                                <td className="py-1 pr-2 whitespace-nowrap text-slate-600">
                                  {r.from_date !== r.to_date ? (
                                    <span>
                                      <span className="line-through opacity-60">{fmtDate(r.from_date)}</span>
                                      <ArrowRight className="w-3 h-3 inline mx-1 text-slate-400" />
                                      <b className="text-rose-700">{fmtDate(r.to_date)}</b>
                                      {r.actor && <span className="text-[10px] text-slate-500"> · by {r.actor}</span>}
                                    </span>
                                  ) : fmtDate(r.from_date)}
                                </td>
                                <td className="py-1 text-right tabular-nums font-semibold">{fmtInr(r.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {l.rows.some((r) => r.detail) && (
                        <div className="text-[10px] text-slate-500 mt-1.5">
                          Notes: {[...new Set(l.rows.map((r) => r.detail).filter(Boolean))].join(' · ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex items-center justify-between px-3 py-2 bg-slate-100 border-t border-slate-200 text-[11px] font-bold text-slate-700">
              <span>Closing · {fmtDate(snapTo)}</span>
              <span className="tabular-nums">{fmtInr(bridge.close)}</span>
            </div>
          </div>
        </div>

        {/* Salesperson leaderboard */}
        {people.leaderboard.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Salesperson accountability · worst cash-share first
            </div>
            <table className="w-full text-[11px] border border-slate-100 rounded overflow-hidden">
              <thead className="bg-slate-50 text-[9px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">Salesperson</th>
                  <th className="text-right px-2 py-1.5">Cash in</th>
                  <th className="text-right px-2 py-1.5">Credit notes</th>
                  <th className="text-right px-2 py-1.5">Pushed out</th>
                  <th className="text-right px-2 py-1.5">Unexplained</th>
                  <th className="text-right px-2 py-1.5">Cash-share</th>
                </tr>
              </thead>
              <tbody>
                {people.leaderboard.map((s, i) => {
                  const share = Math.round(100 * s.cashShare);
                  const tone = share >= 60 ? 'text-emerald-700' : share >= 30 ? 'text-amber-700' : 'text-rose-700';
                  return (
                    <tr key={i} className="border-t border-slate-100" data-testid={`stba-sp-${i}`}>
                      <td className="px-3 py-1 font-medium text-slate-700">{s.name}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{s.cash ? fmtInr(s.cash) : '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-violet-700">{s.cn ? fmtInr(s.cn) : '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-700">{s.slipped ? fmtInr(s.slipped) : '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-red-700">{s.unexplained ? fmtInr(s.unexplained) : '—'}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-bold ${tone}`}>{share}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Actor accountability */}
        {people.actors.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Who moved the numbers
            </div>
            <table className="w-full text-[11px] border border-slate-100 rounded overflow-hidden">
              <thead className="bg-slate-50 text-[9px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">Actor</th>
                  <th className="text-right px-2 py-1.5">CNs</th>
                  <th className="text-right px-2 py-1.5">CN value</th>
                  <th className="text-right px-2 py-1.5">Dates pushed</th>
                  <th className="text-right px-2 py-1.5">Push value</th>
                </tr>
              </thead>
              <tbody>
                {people.actors.map((a, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`stba-actor-${i}`}>
                    <td className="px-3 py-1 font-medium text-slate-700">{a.actor}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{a.cn || '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-violet-700">{a.cn ? fmtInr(a.cnValue) : '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{a.slipped || '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-rose-700">{a.slipped ? fmtInr(a.slippedValue) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Repeat offenders */}
        {people.repeatCust.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Repeat-offender flags</div>
            <div className="space-y-1">
              {people.repeatCust.map((c, i) => (
                <div key={i} className="text-[11px] rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-rose-800 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span><b>{c.customer}</b> — hit by {c.groups.size} non-cash causes ({[...c.groups].join(', ')}) · {fmtInr(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-[10px] text-slate-400 leading-relaxed border-t border-slate-100 pt-2">
          How to read this: every line is computed invoice-by-invoice, so the bridge always adds up
          exactly from opening to closing. “Promise pushed out of window” means the money is still
          owed — only the promised date now falls outside {rangeFrom} → {rangeTo}. “Credit notes” and
          “Written off” reduce the number with no cash. Only “Cash collected” is real bank money.
        </div>
      </DialogContent>
    </Dialog>
  );
}
